/**
 * Application state for the runtime.
 *
 * This app keeps a single tree of state at the top of the component tree and
 * prop-drills data down and action callbacks back up. Only a handful of places
 * touch the store, and nothing below the root subscribes independently.
 *
 * A single {@link flowReducer} owns two page states - `base` and the optional
 * `modal` shown over it - selected by an action's {@link Target}.
 */
import { type AppState, PageType } from '../types/models';
import type {
    Container,
    ObjectData,
    ObjectDataProperty,
    PageElement,
} from '../types/public/models';
import { emptyAppState, getElementById, isComponent, updateElementTree } from '../utils/state';

/** Which of the two application states an action targets. */
export type Target = 'base' | 'modal';

/** Holds the base page state and the (optional) modal page state shown over it. */
export type FlowState = { base: AppState; modal: AppState };

export type MergeObjectDataProperty = (
    existingProperty: ObjectDataProperty,
    newProperty: ObjectDataProperty,
) => ObjectDataProperty;

export type FlowAction =
    | { type: 'SET_STATE'; target: Target; appState: AppState }
    | { type: 'SET_ERROR'; message: string | null }
    | { type: 'SET_LOADING'; target: Target }
    | {
          type: 'UPDATE_ELEMENT';
          target: Target;
          elementId: string;
          elementPartial: Partial<PageElement>;
      }
    | {
          type: 'UPDATE_OBJECT_DATA';
          target: Target;
          elementId: string;
          objectData: ObjectData[];
          fn?: MergeObjectDataProperty | undefined;
      }
    | { type: 'APPLY_MOVE_RESULT'; result: AppState | undefined }
    | { type: 'APPLY_CONDITION_RESULT'; result: AppState | undefined };

export const emptyFlowState: FlowState = { base: emptyAppState, modal: emptyAppState };

/**
 * Merges incoming objectData into a component, mirroring the previous
 * `updateObjectData` reducer in appState.ts.
 */
const applyObjectData = (
    state: AppState,
    {
        elementId,
        objectData,
        fn,
    }: { elementId: string; objectData: ObjectData[]; fn?: MergeObjectDataProperty | undefined },
): AppState => {
    if (!state.rootElement) {
        throw new Error('Could not get rootElement');
    }

    const element = getElementById(state.rootElement, elementId);

    if (!element) {
        throw new Error(`Could not find element with id ${elementId}`);
    }

    if (!isComponent(element)) {
        throw new Error(`Element ${element.developerName} is not a component`);
    }

    const existingObjectData = JSON.parse(JSON.stringify(element.objectData || [])) as ObjectData[];

    for (const incoming of objectData) {
        const matchingObjectData = existingObjectData.find(
            (item) => item.externalId === incoming.externalId,
        );

        if (matchingObjectData) {
            matchingObjectData.properties = matchingObjectData.properties.map(
                (existingProperty) => {
                    const newProperty = incoming.properties.find(
                        (p) => p.typeElementPropertyId === existingProperty.typeElementPropertyId,
                    );

                    if (!newProperty) {
                        // TODO: determine what to do if a net-new property is trying to be added
                        return existingProperty;
                    }

                    if (
                        newProperty.contentType === 'ContentObject' ||
                        newProperty.contentType === 'ContentList'
                    ) {
                        // TODO: recurse here
                        return newProperty;
                    }

                    if (fn) {
                        return fn(existingProperty, newProperty);
                    }

                    return { ...existingProperty, contentValue: newProperty.contentValue };
                },
            );
        } else {
            existingObjectData.push(incoming);
        }
    }

    return {
        ...state,
        rootElement: updateElementTree(
            elementId,
            {
                objectData: existingObjectData,
                selectedItems: existingObjectData.filter((item) => item.isSelected),
            },
            state.rootElement,
        ) as Container,
    };
};

export interface FlowStore {
    getState: () => FlowState;
    dispatch: (action: FlowAction) => void;
    subscribe: (listener: () => void) => () => void;
}

export const createFlowStore = (initialState: FlowState): FlowStore => {
    let state = initialState;
    const listeners = new Set<() => void>();

    return {
        getState: () => state,
        dispatch: (action) => {
            state = flowReducer(state, action);
            for (const listener of listeners) {
                listener();
            }
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
};

/**
 * Single reducer for both the base and modal page states.
 *  - a forward move returning a Default page updates the base and closes the modal;
 *  - a page condition returning a Default page updates the base but leaves the modal.
 */
export function flowReducer(state: FlowState, action: FlowAction): FlowState {
    switch (action.type) {
        case 'SET_STATE':
            return {
                ...state,
                [action.target]: { ...action.appState, errorMessage: null },
            };

        case 'SET_ERROR':
            return {
                ...state,
                base: { ...state.base, errorMessage: action.message, isLoading: false },
            };

        case 'SET_LOADING':
            return {
                ...state,
                [action.target]: { ...state[action.target], isLoading: true },
            };

        case 'UPDATE_ELEMENT': {
            const slice = state[action.target];
            return {
                ...state,
                [action.target]: {
                    ...slice,
                    rootElement: updateElementTree(
                        action.elementId,
                        action.elementPartial,
                        slice.rootElement,
                    ) as Container,
                },
            };
        }

        case 'UPDATE_OBJECT_DATA':
            return {
                ...state,
                [action.target]: applyObjectData(state[action.target], action),
            };

        case 'APPLY_MOVE_RESULT': {
            const { result } = action;
            return {
                base:
                    result?.pageType === PageType.Default
                        ? { ...result, errorMessage: null, isLoading: false }
                        : { ...state.base, errorMessage: null, isLoading: false },
                modal:
                    result?.pageType === PageType.Modal
                        ? { ...result, errorMessage: null, isLoading: false }
                        : emptyAppState,
            };
        }

        case 'APPLY_CONDITION_RESULT': {
            const { result } = action;
            return {
                base:
                    result?.pageType === PageType.Default
                        ? { ...result, errorMessage: null, isLoading: false }
                        : { ...state.base, errorMessage: null, isLoading: false },
                modal:
                    result?.pageType === PageType.Modal
                        ? { ...result, isLoading: false }
                        : state.modal,
            };
        }

        default:
            return state;
    }
}
